import { Injectable, Injector } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { createSyncCustomValidators } from '@app/finxone-web-frontend/app/lib/utils/validators/create-custom-validators';
import { FormID, RailTypeEnum } from '@finxone-platform/form-action';
import {
  ACCOUNTTYPE,
  BeneficiaryRequirementsResponse,
  FormFieldConfig,
} from '@finxone-platform/shared/sys-config-types';
import { ValidationConfig } from '@finxone-platform/ui-components';
import { Observable, take, tap } from 'rxjs';

export interface BeneficiaryFormType {
  type: ACCOUNTTYPE.PERSONAL | ACCOUNTTYPE.BUSINESS;
  country: string;
  currency: string;
  paymentRail: Pick<typeof RailTypeEnum, 'SWIFT' | 'LOCAL' | 'SEPA_TARGET_2'>;
  accountDetails: {
    nickName: string;
    fullName: string;
    accountNumber?: string;
    sortCode?: string;
    iban?: string;
    BIC?: string;
    aba?: string;
    rtnCanada?: string;
    clabe?: string;
    bsbCode?: string;
    ifsc?: string;
    cnaps?: string;
    bankId?: string;
    bankName?: string;
    bankCode?: string;
    branchCode?: string;
  };
  beneficiaryAddress?: {
    addressLine1: string;
    addressLine2?: string;
    addressLine3?: string;
    addressLine4?: string;
    houseNumber?: string;
    refinement?: string;
    city: string;
    stateProvinceRegion?: string;
    postalCode: string;
    country: string;
  };
  id?: string;
}

export type FormGroupControls<T> = {
  [K in keyof T]?: FormControl | FormGroup;
};

const beneficiaryFormConfig: FormFieldConfig[] = [
  { name: 'type', type: 'select', defaultValue: ACCOUNTTYPE.PERSONAL, label: 'Account Type' },
  { name: 'country', type: 'text', defaultValue: '', label: 'Country' },
  { name: 'currency', type: 'text', defaultValue: '', label: 'Currency' },
  { name: 'paymentRail', type: 'select', defaultValue: '', label: 'Payment Rail' },
  {
    name: 'accountDetails',
    type: 'group',
    label: 'Account Details',
    fields: [
      { name: 'nickName', type: 'text', defaultValue: '', label: 'Nickname' },
      { name: 'fullName', type: 'text', defaultValue: '', label: 'Full Name' },
      { name: 'accountNumber', type: 'text', defaultValue: '', label: 'Account Number' },
      { name: 'sortCode', type: 'text', defaultValue: '', label: 'Sort Code' },
      { name: 'iban', type: 'text', defaultValue: '', label: 'IBAN' },
      { name: 'BIC', type: 'text', defaultValue: '', label: 'BIC Code' },
      { name: 'aba', type: 'text', defaultValue: '', label: 'ABA Routing Number' },
      { name: 'rtnCanada', type: 'text', defaultValue: '', label: 'RTN Canada' },
      { name: 'clabe', type: 'text', defaultValue: '', label: 'CLABE' },
      { name: 'bsbCode', type: 'text', defaultValue: '', label: 'BSB Code' },
      { name: 'ifsc', type: 'text', defaultValue: '', label: 'IFSC Code' },
      { name: 'cnaps', type: 'text', defaultValue: '', label: 'CNAPS Code' },
      { name: 'bankId', type: 'text', defaultValue: '', label: 'Bank ID' },
      { name: 'bankName', type: 'text', defaultValue: '', label: 'Bank Name' },
      { name: 'bankCode', type: 'text', defaultValue: '', label: 'Bank Code' },
      { name: 'branchCode', type: 'text', defaultValue: '', label: 'Branch Code' },
    ],
  },
  {
    name: 'beneficiaryAddress',
    type: 'group',
    label: 'Beneficiary Address',
    fields: [
      { name: 'addressLine1', type: 'text', defaultValue: '', label: 'Address Line 1' },
      { name: 'addressLine2', type: 'text', defaultValue: '', label: 'Address Line 2' },
      { name: 'addressLine3', type: 'text', defaultValue: '', label: 'Address Line 3' },
      { name: 'addressLine4', type: 'text', defaultValue: '', label: 'Address Line 4' },
      { name: 'houseNumber', type: 'text', defaultValue: '', label: 'House Number' },
      { name: 'refinement', type: 'text', defaultValue: '', label: 'Refinement' },
      { name: 'city', type: 'text', defaultValue: '', label: 'City' },
      { name: 'stateProvinceRegion', type: 'text', defaultValue: '', label: 'State' },
      { name: 'postalCode', type: 'text', defaultValue: '', label: 'Postal Code' },
      { name: 'country', type: 'text', defaultValue: '', label: 'Country' },
    ],
  },
  {
    name: 'id',
    type: 'text',
    defaultValue: '',
    label: 'Beneficiary ID',
  },
];

@Injectable({ providedIn: 'root' })
export class BeneficiaryFormService {
  private readonly FORM_KEY = FormID.BENEFICIARY_FORM;
  private form: FormGroup<FormGroupControls<BeneficiaryFormType>>;

  constructor(
    private workflowFormsService: WorkflowFormsService,
    private accountService: AccountService,
    private injector: Injector,
  ) {
    // Initialise all controls using WorkflowFormsService
    this.workflowFormsService.createForm(this.FORM_KEY, beneficiaryFormConfig, true);
    this.form = this.workflowFormsService.getFormPart(this.FORM_KEY) as FormGroup<
      FormGroupControls<BeneficiaryFormType>
    >;
    this.workflowFormsService.registerCurrentPageFormWidgetForm(this.FORM_KEY);
  }

  /**
   * Returns validation messages for given field config.
   */
  public getValidationConfig(validation: FormFieldConfig['validation']): { type: string; message: string }[] {
    const validations: ValidationConfig[] = [];
    if (validation?.required) {
      validations.push({ type: 'required', message: 'This field is required' });
    }
    if (validation?.pattern) {
      validations.push({ type: 'pattern', message: 'Invalid format', params: validation.pattern });
    }

    if (validation?.validators) {
      const customValidators = createSyncCustomValidators(validation?.validators, this.injector);

      validations.push(
        ...customValidators.map((validator) => {
          return { type: '', message: '', validatorFn: validator };
        }),
      );
    }

    return validations;
  }

  /**
   * Public getter for the beneficiary form instance.
   */
  public get beneficiaryForm(): FormGroup<FormGroupControls<BeneficiaryFormType>> {
    return this.form;
  }

  public getAndSetBeneficiaryRequirements(): Observable<BeneficiaryRequirementsResponse> {
    let paymentScheme = this.form.get('paymentRail')?.value;

    if (paymentScheme === '') {
      paymentScheme = undefined;
    }

    return this.accountService
      .getBeneficiaryRequirements(
        this.form.get('country')?.value,
        this.form.get('currency')?.value,
        paymentScheme,
      )
      .pipe(
        take(1),
        tap((response: BeneficiaryRequirementsResponse) => {
          const bankAccountReqs = response?.beneficiaryBankAccount;
          this.resetRoutingCodeValidators();
          // Only remove validators from controls that are not required
          if (bankAccountReqs) {
            // Full Name is always required for bank accounts
            this.workflowFormsService.addValidatorsToControl('beneficiaryForm.accountDetails', 'fullName', [
              Validators.required,
            ]);

            // IBAN
            if (bankAccountReqs.iban?.requirement === 'required') {
              const validators: ValidatorFn[] = [Validators.required];
              if (bankAccountReqs.iban.regex) {
                validators.push(Validators.pattern(bankAccountReqs.iban.regex));
              }
              this.workflowFormsService.removeValidatorsFromControl('beneficiaryForm.accountDetails', 'iban');
              this.workflowFormsService.addValidatorsToControl(
                'beneficiaryForm.accountDetails',
                'iban',
                validators,
              );
            } else {
              this.workflowFormsService.removeValidatorsFromControl('beneficiaryForm.accountDetails', 'iban');
              this.beneficiaryForm.get('accountDetails.iban')?.setValue('');
            }

            // Account Number
            if (bankAccountReqs.accountNumber?.requirement === 'required') {
              const validators: ValidatorFn[] = [Validators.required];
              if (bankAccountReqs.accountNumber.regex) {
                validators.push(Validators.pattern(bankAccountReqs.accountNumber.regex));
              }
              this.workflowFormsService.removeValidatorsFromControl(
                'beneficiaryForm.accountDetails',
                'accountNumber',
              );
              this.workflowFormsService.addValidatorsToControl(
                'beneficiaryForm.accountDetails',
                'accountNumber',
                validators,
              );
            } else {
              this.workflowFormsService.removeValidatorsFromControl(
                'beneficiaryForm.accountDetails',
                'accountNumber',
              );
              this.beneficiaryForm.get('accountDetails.accountNumber')?.setValue('');
            }

            // Bank Name
            if (bankAccountReqs.bankName?.requirement === 'required') {
              const validators: ValidatorFn[] = [Validators.required];
              if (bankAccountReqs.bankName.regex) {
                validators.push(Validators.pattern(bankAccountReqs.bankName.regex));
              }
              this.workflowFormsService.removeValidatorsFromControl(
                'beneficiaryForm.accountDetails',
                'bankName',
              );
              this.workflowFormsService.addValidatorsToControl(
                'beneficiaryForm.accountDetails',
                'bankName',
                validators,
              );
            } else {
              this.workflowFormsService.removeValidatorsFromControl(
                'beneficiaryForm.accountDetails',
                'bankName',
              );
              this.beneficiaryForm.get('accountDetails.bankName')?.setValue('');
            }

            // Routing Codes
            if (bankAccountReqs.routingCodes) {
              Object.entries(bankAccountReqs.routingCodes).forEach(([key, value]) => {
                let controlName = '';
                switch (key) {
                  case 'bic':
                    controlName = 'BIC';
                    break;
                  case 'aba':
                    controlName = 'routingNumber';
                    break;
                  case 'bank-code':
                    controlName = 'sortCode';
                    break;
                  case 'branch-code':
                    controlName = 'branchCode';
                    break;
                  case 'rtn-canada':
                    controlName = 'rtnCanada';
                    break;
                  case 'sort-code':
                    controlName = 'sortCode';
                    break;
                  case 'bsb-code':
                    controlName = 'bsbCode';
                    break;
                  case 'cnaps':
                    controlName = 'cnaps';
                    break;
                  case 'ifsc':
                    controlName = 'ifsc';
                    break;
                  case 'clabe':
                    controlName = 'clabe';
                    break;
                  default:
                    controlName = key;
                }
                if (value?.requirement === 'required') {
                  const validators: ValidatorFn[] = [Validators.required];
                  if (value.regex) {
                    validators.push(Validators.pattern(value.regex));
                  }
                  this.workflowFormsService.addValidatorsToControl(
                    'beneficiaryForm.accountDetails',
                    `${controlName}`,
                    validators,
                  );
                } else {
                  this.beneficiaryForm.get(`accountDetails.${controlName}`)?.setValue('');
                }
              });
            }
          }

          // Beneficiary Address requirements (if present)
          const addressReqs = response?.beneficiaryAddress;
          const paymentRail = this.beneficiaryForm.get('paymentRail')?.value;
          if (
            addressReqs &&
            (bankAccountReqs?.requirements?.beneficiaryAddress === 'required' ||
              paymentRail === RailTypeEnum.SEPA_TARGET_2)
          ) {
            Object.entries(addressReqs).forEach(([key, value]) => {
              if (value?.requirement === 'required') {
                const validators: ValidatorFn[] = [Validators.required];
                if (value.regex) {
                  validators.push(Validators.pattern(value.regex));
                }
                this.workflowFormsService.addValidatorsToControl(
                  'beneficiaryForm.beneficiaryAddress',
                  `${key}`,
                  validators,
                );
              } else {
                this.workflowFormsService.removeValidatorsFromControl(
                  'beneficiaryForm.beneficiaryAddress',
                  `${key}`,
                );
              }
            });
          } else if (addressReqs) {
            Object.entries(addressReqs).forEach(([key]) => {
              this.workflowFormsService.removeValidatorsFromControl(
                'beneficiaryForm.beneficiaryAddress',
                `${key}`,
              );
            });
          } else {
            // NOTE: We are resetting here in case of I begin a USD + US beneficary (with address), then switch to GBP + GBP (no address).
            // If this causes problems in future where I go back to the account details page triggering this then back to address and address is cleared,
            // we may need to rethink this logic. For now, this is the simplest way to ensure address is cleared when not required.
            const addressFormGroup = this.beneficiaryForm.get('beneficiaryAddress') as FormGroup;
            // No address requirements, reset form
            addressFormGroup.reset();
            // No address requirements, remove all validators
            Object.keys(addressFormGroup.controls ?? {}).forEach((key) => {
              this.workflowFormsService.removeValidatorsFromControl(
                'beneficiaryForm.beneficiaryAddress',
                key,
              );
            });
          }

          this.refreshFormInstance();
        }),
      );
  }

  /**
   * Refreshes the internal form instance from the WorkflowFormsService.
   */
  private refreshFormInstance(): void {
    // Optionally update the form reference if needed from WorkflowFormsService
    const refreshed = this.workflowFormsService.getFormPart(this.FORM_KEY);
    if (refreshed) {
      this.form = refreshed as FormGroup<FormGroupControls<BeneficiaryFormType>>;
    }
  }

  private resetRoutingCodeValidators(): void {
    const controlNames = [
      'BIC',
      'routingNumber',
      'sortCode',
      'branchCode',
      'rtnCanada',
      'cnaps',
      'ifsc',
      'clabe',
      'bsbCode',
    ];
    controlNames.forEach((name) => {
      this.workflowFormsService.removeValidatorsFromControl('beneficiaryForm.accountDetails', `${name}`);
    });
  }
}
